import React, { useMemo, useRef, useState } from 'react';
import { WandSparkles, ArrowUp, ArrowDown, X } from 'lucide-react';
import { toMin, fromMin } from './plannerShared';

// Individual-meeting slots inside a block's panel: one list per window (the whole block, or each
// group's half of a rotation). Slots follow each other from the window's start, so the order is
// the schedule. Reorder by dragging or with the arrows, add or remove players, or Auto-fill
// (group order / named players). Each player only ever sees their own slot.

export default function SlotsEditor({ block, players, groups, canEdit, busy, stale, onSave, onAutofill }) {
  const initial = () => Object.fromEntries(block.slot_windows.map(w => [
    String(w.group ?? 0),
    block.slots.filter(s => (s.group ?? 0) === (w.group ?? 0)).sort((a, b) => a.index - b.index).map(s => s.player_id),
  ]));
  const [order, setOrder] = useState(initial);
  const dragRef = useRef(null);
  const nameOf = useMemo(() => Object.fromEntries(players.map(p => [p.id, p.name])), [players]);
  const slotted = new Set(Object.values(order).flat());
  const dirty = JSON.stringify(order) !== JSON.stringify(initial());

  const move = (key, from, to) => setOrder(prev => {
    const list = [...prev[key]];
    if (to < 0 || to >= list.length) return prev;
    const [x] = list.splice(from, 1);
    list.splice(to, 0, x);
    return { ...prev, [key]: list };
  });
  const remove = (key, i) => setOrder(prev => ({ ...prev, [key]: prev[key].filter((_, j) => j !== i) }));
  const add = (key, id) => setOrder(prev => ({ ...prev, [key]: [...prev[key], Number(id)] }));

  if (stale) {
    return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Save the block first; the slot windows follow its times and slot length.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {block.slot_windows.map(w => {
        const key = String(w.group ?? 0);
        const list = order[key] || [];
        const groupIds = w.group ? new Set(groups[w.group] || []) : null;
        const addable = players.filter(p => !slotted.has(p.id) && (!groupIds || groupIds.has(p.id)));
        return (
          <div key={key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>
              {w.group ? `Group ${w.group} · ` : ''}{w.start}–{w.end} · {list.length}/{w.capacity} slots of {block.slot_minutes} min
            </div>
            {list.map((pid, i) => {
              const start = toMin(w.start) + i * block.slot_minutes;
              const fits = start + block.slot_minutes <= toMin(w.end);
              const wrongGroup = groupIds && !groupIds.has(pid);
              return (
                <div key={pid}
                  draggable={canEdit}
                  onDragStart={() => { dragRef.current = { key, i }; }}
                  onDragOver={e => { if (dragRef.current?.key === key) e.preventDefault(); }}
                  onDrop={() => { const d = dragRef.current; dragRef.current = null; if (d && d.key === key) move(key, d.i, i); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '2px 0', cursor: canEdit ? 'grab' : 'default' }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 12, width: 92, color: fits ? 'var(--text-secondary)' : 'var(--pl-err)' }}>
                    {fromMin(start)}–{fromMin(start + block.slot_minutes)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {nameOf[pid] || `Player #${pid}`}
                    {!fits && <span style={{ color: 'var(--pl-err)', fontSize: 11 }}> · doesn't fit</span>}
                    {wrongGroup && <span style={{ color: 'var(--pl-warn-border)', fontSize: 11 }}> · not in Group {w.group} anymore</span>}
                  </span>
                  {canEdit && (
                    <>
                      <button type="button" className="pl-btn" style={{ minHeight: 24, padding: '1px 5px' }} aria-label="Earlier" disabled={i === 0} onClick={() => move(key, i, i - 1)}><ArrowUp size={12} /></button>
                      <button type="button" className="pl-btn" style={{ minHeight: 24, padding: '1px 5px' }} aria-label="Later" disabled={i === list.length - 1} onClick={() => move(key, i, i + 1)}><ArrowDown size={12} /></button>
                      <button type="button" className="pl-btn" style={{ minHeight: 24, padding: '1px 5px' }} aria-label="Remove slot" onClick={() => remove(key, i)}><X size={12} /></button>
                    </>
                  )}
                </div>
              );
            })}
            {canEdit && list.length < w.capacity && addable.length > 0 && (
              <select className="pl-input" value="" aria-label="Add a player" onChange={e => e.target.value && add(key, e.target.value)}>
                <option value="">+ Add a player…</option>
                {addable.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>
        );
      })}
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" className="pl-btn" disabled={busy} onClick={onAutofill} title="Group order, or the named players in their order"><WandSparkles size={14} /> Auto-fill</button>
          <button type="button" className="pl-btn pl-btn--primary" disabled={busy || !dirty}
            onClick={() => onSave(block.slot_windows.map(w => ({ group: w.group, player_ids: order[String(w.group ?? 0)] || [] })))}>Save slots</button>
        </div>
      )}
    </div>
  );
}
